import defaultConfig from '../constants/navigation.json';
import { groupBy, keyBy, compactArray, toCapitalize } from './helpers';

let config = {};
let _allChallenges = null;

export const setNavigationConfig = (newConfig) => {
  config = newConfig;
  _allChallenges = null;
};

setNavigationConfig(defaultConfig);

export const getActiveAndInactiveChallenges = () => {
  if (_allChallenges) {
    return _allChallenges;
  }

  const triggersByChallengeId = groupBy(config.triggers, 'challengeId');
  const triggersByFoodGroupId = groupBy(config.triggers, 'foodGroupId');

  const foodGroupCodes = {};

  config.foodGroups.forEach((foodGroup) => {
    if (!foodGroup.code) {
      return;
    }

    foodGroupCodes[foodGroup.id] = [...(foodGroupCodes[foodGroup.id] || []), foodGroup.code];

    if (foodGroup.parentId) {
      foodGroupCodes[foodGroup.parentId] = [...(foodGroupCodes[foodGroup.parentId] || []), foodGroup.code];
    }
  });

  _allChallenges = config.challenges
    .map((challenge) => ({
      id: challenge.id,
      foodstandId: challenge.foodstandId,
      name: challenge.name,
      priority: challenge.priority,
      active: challenge.active,
      triggers: (triggersByChallengeId[challenge.id] || []).map((trigger) => ({
        codes: foodGroupCodes[trigger.foodGroupId] || [],
        condition: trigger.condition,
        check: new Function('id', 'ideal', `return ${trigger.condition}`),
      })),
    }))
    .sort((c1, c2) => c1.priority - c2.priority);

  return _allChallenges;
};

export const getActiveChallenges = () => getActiveAndInactiveChallenges().filter((challenge) => challenge.active);

export const getDietFoodGroupValue = (diet, foodGroup) => {
  if (foodGroup.children && !foodGroup.code) {
    return foodGroup.children.some((foodGroup) => typeof diet.food_group_values[foodGroup.code] === 'number')
      ? foodGroup.children
          .map((foodGroup) => diet.food_group_values[foodGroup.code] || 0)
          .reduce((sum, value) => sum + value, 0)
      : null;
  }

  return typeof diet.food_group_values[foodGroup.code] === 'number' ? diet.food_group_values[foodGroup.code] : null;
};

export const getFoodGroupChangeInstructions = (transitionData, direction, showChildren = true) => {
  const changes = [];

  transitionData
    .sort((a, b) => {
      const bandA = a.absoluteDifference;
      const bandB = b.absoluteDifference;
      let comparison = 0;
      if (bandA > bandB) {
        comparison = 1;
      } else if (bandA < bandB) {
        comparison = -1;
      }
      return comparison * -1;
    })
    .forEach((change) => {
      let label = change.label;
      let shouldInclude = false;

      if (direction === 'increase' && change.difference >= 0.5 && !change.parentInfo.suppress_increase) {
        // && !change.parentInfo.suppress_increase
        shouldInclude = true;
      }

      if (direction === 'decrease' && change.difference <= 0.5 && !change.parentInfo.suppress_decrease) {
        // && !change.parentInfo.suppress_decrease
        shouldInclude = true;
      }

      if (shouldInclude) {
        const childrenToShow = change.children.filter(
          (c) =>
            c.include_subgroup &&
            c.label
              .toLowerCase()
              .replace(
                /&/g,
                'and' !== label.replace(/&/g, 'and') &&
                  ((c.difference > 0 && direction === 'increase') || (c.difference < 0 && direction === 'decrease')),
              ),
        );

        if (showChildren && childrenToShow.length > 0) {
          label = `${label} (${childrenToShow.map((c) => c.label).join(', ')})`;
        }

        changes.push({ change, label });
      }
    });

  return changes;
};

export const getFoodGroupChanges = (oldDiet, newDiet, idNutrition, idealNutrition) => {
  const data = { changes: [], changesByKey: {} };
  const oldGroups = Object.entries(oldDiet.foodGroupInfo);
  const parents = oldGroups.filter(([key, info]) => !!info.children);

  const IdFiltered =
    idNutrition &&
    idNutrition.filter(function (nutrient) {
      return nutrient.key === 'energy-kilocalories';
    })
      ? idNutrition.filter(function (nutrient) {
          return nutrient.key === 'energy-kilocalories';
        })
      : undefined;
  const idealFiltered =
    idealNutrition &&
    idealNutrition.filter(function (nutrient) {
      return nutrient.key === 'energy-kilocalories';
    })
      ? idealNutrition.filter(function (nutrient) {
          return nutrient.key === 'energy-kilocalories';
        })
      : undefined;
  const dailyCalories = IdFiltered !== undefined && IdFiltered[0] ? IdFiltered[0].value : 2000; // personal calories w/ 2k fallback
  const dailyIdealCalories = idealFiltered !== undefined && idealFiltered[0] ? idealFiltered[0].value : 2000; // personal calories w/ 2k fallback
  const personalized = (num) => {
    return (num / 2000) * dailyCalories;
  };
  const personalizedIdeal = (num) => {
    return (num / 2000) * dailyIdealCalories;
  };

  parents.forEach(([key, parentInfo]) => {
    const oldValue = personalized(parentInfo.value);
    const newValue = newDiet && newDiet.foodGroupInfo[key] ? personalizedIdeal(newDiet.foodGroupInfo[key].value) : 0;

    if (oldValue > 0 || newValue > 0) {
      const children = [];
      Object.entries(parentInfo.children).forEach(([childKey, childInfo]) => {
        const oldInfo = oldDiet.foodGroupInfo[childKey];
        const newInfo = newDiet.foodGroupInfo[childKey];

        if (!oldInfo && !newInfo) {
          throw new Error(`Missing ${childKey}`);
        }

        const oldChildValue = personalized(oldInfo.value);
        const newChildValue = newInfo.value ? personalizedIdeal(newInfo.value) : 0;

        children.push({
          ...childInfo,
          key: childKey,
          label: toCapitalize(oldInfo.label.toLowerCase()),
          oldValue: oldChildValue,
          newValue: newChildValue,
          difference: newChildValue - oldChildValue,
        });
      });

      const change = {
        key,
        parentInfo,
        oldValue,
        newValue,
        difference: newValue - oldValue,
        absoluteDifference: Math.abs(newValue - oldValue),
        label: toCapitalize(parentInfo.label.toLowerCase()),
        children: children.sort((child1, child2) => child1.label.localeCompare(child2.label)),
      };

      data.changes.push(change);
      data.changesByKey[change.key] = change;
    }
  });
  return data;
};

export const getFoodGroupChangeValue = (idDiet, idealDiet, idNutrition, idealNutrition) => {
  if (!idDiet || !idealDiet) {
    return null;
  }

  const { changes, changesByKey } = getFoodGroupChanges(idDiet, idealDiet, idNutrition, idealNutrition);

  return {
    increase: getFoodGroupChangeInstructions(changes, 'increase'),
    decrease: getFoodGroupChangeInstructions(changes, 'decrease'),
    changes,
    changesByKey,
  };
};

const getDietInfo = (diet, foodGroupCodes) => {
  const foodGroupQuantity = foodGroupCodes.some((code) => typeof diet.food_group_values[code] === 'number')
    ? foodGroupCodes.map((code) => diet.food_group_values[code] || 0).reduce((sum, value) => sum + value, 0)
    : null;

  return {
    code: diet.code,
    quality: diet.quality,
    foodGroupQuantity,
  };
};

export const checkTrigger = (idDiet, idealDiet, trigger, debug) => {
  const idInfo = getDietInfo(idDiet, trigger.codes);
  const idealInfo = getDietInfo(idealDiet, trigger.codes);

  const active =
    (!trigger.condition.includes('foodGroupQuantity') ||
      (idInfo.foodGroupQuantity !== null && idealInfo.foodGroupQuantity !== null)) &&
    trigger.check(idInfo, idealInfo);

  return debug ? { active, idInfo, idealInfo } : active;
};

const precludedChallenges = {
  DairyFree: [
    36, // Remove Dairy
    35, // Eat Less Dairy
    42, // Increase Dairy
    43, // Add Dairy
  ],
  GlutenFree: [
    27, // Eat 2 Servings of Whole Grains A Day
  ],
  WheatFree: [
    27, // Eat 2 Servings of Whole Grains A Day
  ],
  ShellfishFree: [
    41, // Increase Fish
    40, // Add Fish
    33, // Remove Fish
    32, // Eat Less Fish
  ],
  AlcoholFree: [
    15, // Drink Less Booze
  ],
  EggFree: [
    34, // Remove Eggs
  ],
};

export const restrictionsPrecludeChallenge = (dietRestrictions, challenge) =>
  (dietRestrictions || []).some((restriction) => (precludedChallenges[restriction] || []).includes(challenge.id));

export const triggersChallenge = (idDiet, idealDiet, dietRestrictions, challenge) =>
  !restrictionsPrecludeChallenge(dietRestrictions, challenge) &&
  challenge.triggers.some((trigger) => checkTrigger(idDiet, idealDiet, trigger));

export const isIdIdeal = (idDiet, idealDiet) => idDiet.id === idealDiet.id;

export const idealIdChallenges = [
  16, // Eat Real Food
  8, // Avoid Added Sugar
  24, // Fill half or more of your plate with Fruits and Veggies
  28, // Eat 7 Servings of Fruits and Veggies a Day
  21, // Don't Eat 2 Hours Before Bed
  20, // Drink 8 Glasses of Water a Day
  16, // No Food Waste
  13, // Eat Less Animal Products
  37, // Eat More Plant-based Proteins
  18, // Avoid Sugar at Breakfast
  44, // Eat When You're Hungry
  14, // Make Your Lunch More Often
  27, // Eat 2 Servings of Whole Grains A Day
  26, // Avoid Sweetened Beverages
  9, // Eat One Bite At A Time
  22, // Eat 5 Servings of Fruits & Veggies Per Day
  25, // Avoid Fast Food
  19, // Avoid Sweets, Dessert, and Candy
  3, // Eat At Least 1 Distraction-Free Meal Per Day
  7, // Eat 3 Servings of Fruits & Veggies Per Day
  11, // Cook Dinner More Often
  1, // Avoid Soda
  34, // Remove Eggs
  33, // Remove Fish
  32, // Eat Less Fish
  31, // Remove Poultry
  30, // Eat Less Poultry
  29, // Remove Red Meat
  15, // Drink Less Booze
];

export const getChallenges = (idDiet, idealDiet, dietRestrictions) => {
  if (isIdIdeal(idDiet, idealDiet)) {
    const challengesById = keyBy(getActiveChallenges(), 'foodstandId');

    return compactArray(idealIdChallenges.map((id) => challengesById[id])).filter(
      (challenge) => !restrictionsPrecludeChallenge(dietRestrictions, challenge),
    );
  }

  return getActiveChallenges().filter((challenge) => triggersChallenge(idDiet, idealDiet, dietRestrictions, challenge));
};

export const getChallengesWithIds = (challengeIds) =>
  getActiveAndInactiveChallenges().filter((challenge) => challengeIds.includes(challenge.foodstandId));
